import { getOrganizations } from '@/endpoints/user/getOrganizations';
import type { Organization } from '@/types/organization';
import { utils } from '@/utils';
import { getAuthGeneration, getSessionSubject } from './token';
import { SessionTransitionError } from './operation';

const TTL = 5 * 60 * 1000;

type OrganizationListEntry = {
  subject: string;
  authGeneration: number;
  expiresAt: number;
  organizations: Organization[];
};

let cache: OrganizationListEntry | null = null;
let invalidationEpoch = 0;
let requestSeq = 0;
let highestPublishedSeq = 0;
let inFlight: {
  subject: string;
  authGeneration: number;
  epoch: number;
  forced: boolean;
  promise: Promise<Organization[]>;
} | null = null;
const listeners = new Set<() => void>();

const notify = (): void => {
  for (const listener of listeners) listener();
};

export const subscribeOrganizationList = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const peekOrganizationList = (): Organization[] | null => {
  const subject = getSessionSubject();
  const authGeneration = getAuthGeneration();
  if (!subject || !cache || cache.subject !== subject || cache.authGeneration !== authGeneration || cache.expiresAt <= Date.now()) {
    return null;
  }
  return cache.organizations;
};

// Test-only reset used by the shared unit-test teardown. Subscriptions are
// normally removed by React unmount, but renderToString paths never unmount,
// so a worker-shared test file would otherwise inherit the previous file's
// listeners and its adoptable in-flight request.
export const __resetOrganizationListListenersForTests = (): void => {
  listeners.clear();
  inFlight = null;
};

export const invalidateOrganizationList = (): void => {
  cache = null;
  // Bumping the epoch prevents an in-flight response (started before the
  // invalidation) from repopulating the cache with pre-invalidation data,
  // and stops later callers from adopting that stale request.
  invalidationEpoch += 1;
  notify();
};

export const getOrganizationList = ({
  force = false,
}: {
  force?: boolean;
} = {}): Promise<Organization[]> => {
  const subject = getSessionSubject();
  const authGeneration = getAuthGeneration();
  if (!subject) return Promise.reject(new Error('No authenticated session'));

  if (!force) {
    const existing = peekOrganizationList();
    if (existing) return Promise.resolve(existing);
  }

  const epoch = invalidationEpoch;
  // A forced caller must not adopt a non-forced in-flight request (it may be
  // serving data the caller specifically wants refreshed); the reverse is fine.
  if (
    inFlight &&
    inFlight.subject === subject &&
    inFlight.authGeneration === authGeneration &&
    inFlight.epoch === epoch &&
    (!force || inFlight.forced)
  ) {
    return inFlight.promise;
  }

  const seq = (requestSeq += 1);
  const membershipOnly = utils.authUtils.hasRealmRole('acenda_admin') && !utils.authUtils.isAcendaSuperOrManagement();
  const promise = getOrganizations({ membershipOnly })
    .then((response) => {
      // Identity and cancellation first: a request aborted by a session switch
      // comes back as a canceled error envelope, and surfacing it as a generic
      // Error would let callers toast teardown noise as a real failure.
      if (getSessionSubject() !== subject || getAuthGeneration() !== authGeneration) {
        throw new SessionTransitionError();
      }
      if (response.isError && response.canceled === true) {
        throw new SessionTransitionError();
      }
      if (response.isError) {
        throw new Error(response.errors.error || 'Unable to load organizations');
      }
      if (!Array.isArray(response.result)) {
        throw new Error('Malformed organization response');
      }
      if (invalidationEpoch !== epoch) {
        // This response predates an invalidation and was deliberately not
        // published. Join (or start) the live epoch so direct callers cannot
        // proceed with data no subscriber will ever observe.
        return getOrganizationList();
      }
      // seq guard: a slow earlier response must not clobber the cache written
      // by a later request (e.g. a forced refresh that already completed),
      // and its callers must not observe the superseded body either — an
      // older allow resolving after a published deny would readmit an
      // organization through OrganizationEntryGuard. Rejoin the live list.
      if (seq <= highestPublishedSeq) {
        return getOrganizationList();
      }
      highestPublishedSeq = seq;
      // Keep the previous array identity when the content is unchanged so
      // useSyncExternalStore consumers do not re-render on a refresh that
      // changed nothing.
      const retainedOrganizations =
        cache !== null &&
        cache.subject === subject &&
        cache.authGeneration === authGeneration &&
        JSON.stringify(cache.organizations) === JSON.stringify(response.result)
          ? cache.organizations
          : null;
      cache = {
        subject,
        authGeneration,
        expiresAt: Date.now() + TTL,
        organizations: retainedOrganizations ?? response.result,
      };
      notify();
      return response.result;
    })
    .finally(() => {
      if (inFlight?.promise === promise) inFlight = null;
    });

  inFlight = { subject, authGeneration, epoch, forced: force, promise };
  return promise;
};
